import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AuthStateService } from '../service/auth-state.service';

const REDIRECT_FLAG = 'optima_redirecting_to_login';
const API_ERROR_FLAG = 'optima_api_auth_error';

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {
  constructor(private authStateService: AuthStateService) {}

  /**
   * Intercept HTTP requests to add withCredentials (httpOnly cookie auth)
   * and handle 401/403 errors centrally.
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // If we're already redirecting to login, don't process any more requests
    if (sessionStorage.getItem(REDIRECT_FLAG) === 'true') {
      return EMPTY;
    }

    // Allow /auth/me and /auth/* endpoints to proceed immediately (they initialize auth)
    const isAuthEndpoint = request.url.includes('/api/auth/');
    
    // For non-auth endpoints, wait for auth initialization to complete
    if (!isAuthEndpoint) {
      return this.authStateService.authInitialized$.pipe(
        take(1),
        switchMap(() => this.processRequest(request, next))
      );
    }

    // Auth endpoints proceed immediately
    return this.processRequest(request, next);
  }

  private processRequest(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Clone request with withCredentials so the browser sends the httpOnly auth cookie
    const modifiedRequest = request.clone({
      withCredentials: true
    });

    return next.handle(modifiedRequest).pipe(
      catchError((error) => this.handleAuthError(error, request))
    );
  }

  private handleAuthError(error: HttpErrorResponse, request: HttpRequest<unknown>): Observable<never> {
    // Check if it's a group authorization error (user not authorized for this app)
    // Let AuthService handle these to redirect to forbidden page instead of login
    if (error.status === 401 && error.error?.code === 'UNAUTHORIZED') {
      console.log('🚫 [AUTH INTERCEPTOR] User not authorized (401 UNAUTHORIZED) - letting AuthService handle it');
      return throwError(() => error);
    }

    // Handle 401/403 from ANY endpoint (token expired, invalid, etc.)
    if (error.status === 401 || error.status === 403) {
      // Check if we're already redirecting to prevent loops
      const isRedirecting = sessionStorage.getItem(REDIRECT_FLAG) === 'true';
      const isRedirectingToForbidden = sessionStorage.getItem('optima_forbidden_redirect') === 'true';
      const isRedirectingToApiError = sessionStorage.getItem(API_ERROR_FLAG) === 'true';
      
      if (isRedirecting || isRedirectingToForbidden || isRedirectingToApiError) {
        console.log('⏳ [AUTH INTERCEPTOR] Already redirecting, ignoring', error.status);
        return EMPTY;
      }

      // Determine if this is from an auth endpoint vs a business API
      const isAuthEndpoint = request.url.includes('/api/auth/');
      
      // KEY LOGIC: If auth was already successful and this is NOT from an auth endpoint,
      // then this is a resource authorization issue, NOT an authentication issue
      // → Redirect to error page instead of login (prevents infinite loop)
      if (this.authStateService.authWasSuccessful && !isAuthEndpoint) {
        console.log('⚠️ [AUTH INTERCEPTOR] Got', error.status, 'from', request.url, 
          'but auth was successful - this is an API authorization error, NOT auth failure');
        
        // Set flag to prevent loop
        sessionStorage.setItem(API_ERROR_FLAG, 'true');
        
        // Redirect to forbidden page with error info
        setTimeout(() => {
          globalThis.location.replace('/forbidden/index.html?type=forbidden');
        }, 0);
        
        return EMPTY;
      }

      // Auth endpoint 401 or auth was never successful → redirect to login
      console.log('🔑 [AUTH INTERCEPTOR] Got', error.status, 'from', request.url, '- clearing all state and redirecting to login');
      
      // Set flag FIRST to prevent any other requests from triggering redirect
      sessionStorage.setItem(REDIRECT_FLAG, 'true');
      
      // Clear ALL auth state immediately
      this.clearAllAuthState();
      
      // Reset auth state tracking
      this.authStateService.resetAuthState();
      
      // Use setTimeout to ensure all state is cleared before redirect
      setTimeout(() => {
        globalThis.location.replace('/api/auth/login');
      }, 0);
      
      return EMPTY;
    }

    return throwError(() => error);
  }

  private clearAllAuthState(): void {
    // Tokens are httpOnly cookies cleared by the BFF /auth/logout endpoint
    // Don't remove REDIRECT_FLAG here - it will be cleared on successful login
  }
}
